import { useState } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import PeptideForm from '../components/PeptideForm.jsx'
import Seo from '../components/Seo.jsx'

export default function AdminPeptides() {
  const { user, initializing } = useAuth()
  const { peptides, source, isAdmin, createPeptide, updatePeptide, deletePeptide, seedStarter, missingStarters } = usePeptides()
  const toast = useToast()

  const [editing, setEditing] = useState(null) // peptide | 'new' | null
  const [saving, setSaving] = useState(false)

  if (initializing) return <Seo noindex /> // also the SSG-prerendered state — keep admin out of the index
  if (!user) return <Navigate to="/login" replace state={{ from: '/admin/peptides' }} />
  if (!isAdmin) {
    return (
      <main className="page">
        <div className="container center" style={{ padding: '60px 0' }}>
          <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>🔒</div>
          <h1 className="section-title">Admins only</h1>
          <p className="section-sub">This account isn’t an admin. Add yourself in Supabase (see <code>supabase/peptides.sql</code>).</p>
          <Link to="/peptides" className="btn btn-primary">Back to library</Link>
        </div>
      </main>
    )
  }

  const save = async (payload) => {
    setSaving(true)
    const res = editing === 'new' ? await createPeptide(payload) : await updatePeptide(editing.id, payload)
    setSaving(false)
    if (res?.error) return toast(res.error, 'red')
    toast(editing === 'new' ? 'Peptide added' : 'Saved', 'green')
    setEditing(null)
  }

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can’t be undone.`)) return
    const res = await deletePeptide(p.id)
    toast(res?.error || 'Deleted', res?.error ? 'red' : 'default')
  }

  const seed = async () => {
    const res = await seedStarter()
    toast(res?.error || `Imported ${res.count} starter peptides`, res?.error ? 'red' : 'green')
  }

  if (editing) {
    return (
      <main className="page">
        <div className="container page-narrow">
          <button className="pep-back" onClick={() => setEditing(null)}>← Back to list</button>
          <h1 className="section-title">{editing === 'new' ? 'New peptide' : `Edit ${editing.name}`}</h1>
          <PeptideForm
            initial={editing === 'new' ? null : editing}
            onSave={save}
            onCancel={() => setEditing(null)}
            saving={saving}
          />
        </div>
      </main>
    )
  }

  return (
    <main className="page">
      <div className="container">
        <Link to="/admin" className="pep-back">← Site Admin</Link>
        <div className="between wrap" style={{ marginBottom: 20 }}>
          <div>
            <h1 className="section-title">Manage peptides</h1>
            <p className="section-sub mb-0">Edits go live immediately — no code, no deploy.</p>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {missingStarters > 0 && source !== 'static' && (
              <button className="btn btn-soft btn-sm" onClick={seed}>
                ⬇ Import {source === 'empty' ? 'starter library' : `${missingStarters} new peptide${missingStarters > 1 ? 's' : ''}`}
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>+ New peptide</button>
          </div>
        </div>

        {source === 'empty' && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>Database is empty.</strong>
            <p>You’re seeing the built-in starter profiles. Click <em>Import starter library</em> to copy them
              into the database so you can edit them — or add your own from scratch.</p>
          </div>
        )}
        {source === 'db' && missingStarters > 0 && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>{missingStarters} new starter peptide{missingStarters > 1 ? 's are' : ' is'} available.</strong>
            <p>Click <em>Import {missingStarters} new peptide{missingStarters > 1 ? 's' : ''}</em> above to add the
              latest bundled profiles to your database (your existing entries are untouched).</p>
          </div>
        )}
        {source === 'static' && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>Backend not connected.</strong>
            <p>Peptide editing needs Supabase. Add your keys and run <code>supabase/peptides.sql</code>, then reload.</p>
          </div>
        )}

        <div className="admin-list">
          {peptides.map((p) => (
            <div className="card admin-row" key={p.slug}>
              <div className="admin-row-main">
                <div className="row" style={{ gap: 10 }}>
                  <span className="pill">{p.sortOrder ?? '—'}</span>
                  <strong>{p.name}</strong>
                  <span className="muted small">/{p.slug}</span>
                </div>
                <p className="muted small mb-0" style={{ marginTop: 4 }}>{p.category}</p>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <Link className="btn btn-ghost btn-sm" to={`/peptides/${p.slug}`}>View</Link>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)} disabled={source !== 'db'}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => remove(p)} disabled={source !== 'db'}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  )
}
